import type { MouseEvent, ReactNode } from "react";
import { Icon } from "./Icon";
import type { IconName } from "../data/portfolio";
import { cn } from "../lib/cn";
import { handleSectionLink } from "../lib/scrollToSection";

type Variant = "primary" | "secondary" | "accent" | "ghost";
type Size = "sm" | "md" | "lg";

const BASE =
  "inline-flex items-center gap-2 rounded-full border border-transparent text-sm leading-tight font-semibold whitespace-nowrap transition duration-200 ease-standard hover:-translate-y-0.5";

const SIZES: Record<Size, string> = {
  sm: "px-4 py-2.5",
  md: "px-5 py-3",
  lg: "px-6 py-3.5",
};

const VARIANTS: Record<Variant, string> = {
  primary: "bg-inverse text-on-inverse shadow-lg hover:bg-blue-300",
  secondary:
    "border-hairline bg-subtle text-strong backdrop-blur-md hover:border-hairline-strong hover:bg-white/10",
  accent: "bg-blue-500 text-white shadow-accent hover:bg-blue-400",
  ghost: "bg-transparent text-body hover:bg-subtle hover:text-strong",
};

interface ButtonProps {
  variant?: Variant;
  size?: Size;
  icon?: IconName;
  iconPosition?: "left" | "right";
  /**
   * Renders an anchor instead of a button. Ignored while `disabled`.
   * A `#section` href scrolls in place rather than appending to the URL.
   */
  href?: string;
  disabled?: boolean;
  onClick?: () => void;
  /** Ignored while `href` is set and the button renders as an anchor. */
  type?: "button" | "submit";
  className?: string;
  children?: ReactNode;
}

/** Pill button. Every clickable affordance in this brand is fully rounded and lifts 2px on hover. */
export function Button({
  variant = "primary",
  size = "md",
  icon,
  iconPosition = "right",
  href,
  disabled = false,
  onClick,
  type = "button",
  className,
  children,
}: ButtonProps) {
  const glyph = icon ? <Icon name={icon} size={17} /> : null;
  const classes = cn(
    BASE,
    SIZES[size],
    VARIANTS[variant],
    disabled && "pointer-events-none opacity-45 shadow-none",
    className,
  );

  const body = (
    <>
      {iconPosition === "left" ? glyph : null}
      {children}
      {iconPosition === "right" ? glyph : null}
    </>
  );

  if (href && !disabled) {
    const onAnchorClick = (event: MouseEvent<HTMLAnchorElement>) => {
      handleSectionLink(event, href);
      onClick?.();
    };

    return (
      <a href={href} onClick={onAnchorClick} className={classes}>
        {body}
      </a>
    );
  }

  return (
    <button type={type} onClick={onClick} disabled={disabled} className={classes}>
      {body}
    </button>
  );
}
